import React, {useEffect, useState} from 'react'
import AppContainer from "../../components/container/AppContainer";
import TextInput from "../../components/input/TextInput";
import styled from "@emotion/styled";
import Text from "../../components/text/Text";
import Colors from "../../styles/Colors";
import Section from "../../components/container/Section";
import SelectInput from "../../components/input/SelectInput";
import RadioInput from "../../components/input/RadioInput";
import Button from "../../components/button/Button";
import {includeOnlyNumber, isBoolean, isEmailAddress, isNameOrLastname, isPhoneNumber} from "../../utils/validate";
import {post} from "../../utils/axios";
import withNotification from "../../providers/notifications/NotificationConsumer";
import TextFieldInput from "../../components/input/TextFieldInput";
import Abrevoir from "../../assets/images/Abrevoir.jpg"
import Cloture from "../../assets/images/Cloture.jpg"
import {GAPageView} from "../../services/AnalyticsService";

const MORE_INFO_MAX_LENGTH = 3000

const View = ({showNotification}: any) => {
    const [inputLastnameValue, setInputLastnameValue] = useState("")
    const [inputNameValue, setInputNameValue] = useState("")
    const [selectStatusValue, setSelectStatusValue] = useState("private_individual")
    const [inputEmailValue, setInputEmailValue] = useState("")
    const [inputPhoneValue, setInputPhoneValue] = useState("")
    const [inputAreaSizeValue, setInputAreaSizeValue] = useState("")
    const [selectAreaUnitValue, setSelectAreaUnitValue] = useState("m2")
    const [radioWaterValue, setRadioWaterValue] = useState(false)
    const [radioClosedAreaValue, setRadioClosedAreaValue] = useState(false)
    const [textFieldMoreInfoValue, setTextFieldMoreInfoValue] = useState("")

    const [lastnameError, setLastnameError] = useState("")
    const [nameError, setNameError] = useState("")
    const [emailError, setEmailError] = useState("")
    const [phoneError, setPhoneError] = useState("")
    const [areaError, setAreaError] = useState("")
    const [moreInfoError, setMoreInfoError] = useState("")


    const [isSending, setIsSending] = useState(false)

    const validate = (): boolean => {
        let isValidate = true
        let fillAllField = false

        if (isNameOrLastname(inputLastnameValue)) {
            setLastnameError("")
        } else {
            setLastnameError("Merci d'entrer un nom. Exemple: Dupont")
            isValidate = false
            fillAllField = true
        }

        if (isNameOrLastname(inputNameValue)) {
            setNameError("")
        } else {
            setNameError("Merci d'entrer un prénom. Exemple: Jean pierre")
            isValidate = false
            fillAllField = true
        }

        if (isEmailAddress(inputEmailValue)) {
            setEmailError("")
        } else {
            setEmailError("Merci d'entrer une adresse mail. Exemple: dupont@gmail.com")
            isValidate = false
            fillAllField = true
        }

        if (isPhoneNumber(inputPhoneValue)) {
            setPhoneError("")
        } else {
            setPhoneError("Merci d'entrer un numéro de téléphone. Exemple: 0312345678")
            isValidate = false
            fillAllField = true
        }

        if (inputAreaSizeValue && !includeOnlyNumber(inputAreaSizeValue)) {
            setAreaError("Merci d'entrer une surface valide. Exemple: 2.5")
            isValidate = false
        } else {
            setAreaError("")
        }

        if (textFieldMoreInfoValue && textFieldMoreInfoValue.length > MORE_INFO_MAX_LENGTH) {
            setMoreInfoError("Le nombre maximum de caractères est dépassé: 2000 maximum")
            isValidate = false
        } else {
            setAreaError("")
        }

        if (!isBoolean(radioWaterValue) || !isBoolean(radioClosedAreaValue)) {
            isValidate = false
        }

        if (!["private_individual", "private_company", "public_company"].includes(selectStatusValue)) {
            isValidate = false
        }

        if (!["m2", "a", "ha"].includes(selectAreaUnitValue)) {
            isValidate = false
        }

        if (fillAllField) {
            showNotification({
                title: "Votre demande est invalide.",
                message: "Merci de remplir tous les champs obligatoires.",
                expire: 10000,
                type: "error",
            })
        }

        return isValidate
    }

    const sendEmail = async () => {
        setIsSending(true)

        const data = {
            lastname: inputLastnameValue,
            name: inputNameValue,
            status: selectStatusValue,
            email: inputEmailValue,
            phone: inputPhoneValue,
            areaSize: inputAreaSizeValue || 0,
            areaUnit: selectAreaUnitValue,
            water: radioWaterValue,
            closedArea: radioClosedAreaValue,
            moreInfo: textFieldMoreInfoValue,
        }

        try {
            const {status} = await post({
                url: "/ecopaturhin/contact",
                data,
                additionalHeaders: {
                    'x-api-key': process.env.REACT_APP_API_KEY,
                }
            })

            if (status === 201) {
                showNotification({
                    title: "Demande envoyer",
                    message: "Nous avons bien reçu votre demande",
                    expire: 10000,
                    type: "success",
                })
            }

            setIsSending(false)
        } catch (e) {
            let title = "Un problème est survenu."

            if (e.response?.status === 400)
                title = "Votre demande semble invalide."

            if (e.response?.status === 418)
                title = "Votre demande n'a pas pu aboutir."

            showNotification({
                title,
                message: "Merci de nous contacter si le problème persiste",
                expire: 10000,
                type: "error",
            })
            setIsSending(false)
        }
    }

    const handleSubmit = () => {
        if (!isSending && validate()) {
            sendEmail()
        }
    }

    useEffect(() => {
        GAPageView("OurServices")
    }, []);

    return (
        <AppContainer>
            <Section
                marginVertical={50}
                paddingHorizontal={20}
            >
                <TitleContainer>
                    <Text
                        type={"h4"}
                        color={Colors.green}
                    >
                        Nos prestations
                    </Text>
                </TitleContainer>

                <BoldTextContainer>
                    <Text type={"h6"}>
                        Nos prestations s’adaptent à vos besoins, une solution clé en main
                    </Text>
                </BoldTextContainer>

                <TextContainer>
                    <Text>
                        Tout d’abord nous installons de la clôture amovible ou fixe selon les besoins, un abri, un
                        abreuvoir, un bloc de sel. Ensuite les animaux adaptés à la parcelle.
                    </Text>
                </TextContainer>

                <TextContainer>
                    <Text>
                        Notre équipe assure ensuite des visites de suivi régulières et ne nécessitent aucune action de
                        votre part.
                    </Text>
                </TextContainer>

                <TextContainer>
                    <Text>
                        L’équipe Ecopatu’Rhin reste joignable 24h/24 et 7j/7 en cas de problème vous pouvez nous
                        contacter.
                    </Text>
                </TextContainer>

                <TextContainer>
                    <Text>
                        Nous proposons aussi des animations autour de l’éco pâturage pour sensibiliser le grand public
                        et partager des moments conviviaux.
                    </Text>
                </TextContainer>

                <TextContainer>
                    <Text>
                        Parcelles à partir de 1 000m2.
                    </Text>
                </TextContainer>

                <Text>
                    Contrat à partir de 1 mois
                </Text>
            </Section>

            <Section
                marginVertical={20}
                paddingHorizontal={20}
            >
                <FormTitleContainer>
                    <Text
                        type={"h4"}
                        color={Colors.green}
                    >
                        Nos installations
                    </Text>
                </FormTitleContainer>

                <ImageContainer>
                    <InstallationContainer>
                        <Image src={Abrevoir} alt={"Clôture rigide"}/>

                        <Text>
                            La clôture rigide à mouton de 90cm de haut, non déplaçable, elle convient aux zones bien
                            délimitées. Elle est adaptée aussi bien aux moutons qu’aux chèvres. Ecopatu’Rhin
                            s’occupe de toute la pose.
                        </Text>
                    </InstallationContainer>


                    <InstallationContainer>
                        <Image src={Cloture} alt={"Filet électrique"}/>

                        <Text>
                            Le filet électrique, 100% déplaçable. Celui-ci s’adapte à toutes les surfaces et nécessite
                            un de nos postes électriques autonomes pour une efficacité optimale.
                        </Text>
                    </InstallationContainer>
                </ImageContainer>

                <Text>
                    Dans les deux cas, un abreuvoir d’une contenance suffisante et un abri (si nécessaire) seront mis
                    en place sur vos futures zones.
                </Text>
            </Section>

            <Section
                marginVertical={20}
                paddingHorizontal={20}
            >
                <FormTitleContainer>
                    <Text
                        type={"h4"}
                        color={Colors.green}
                    >
                        Nos tarifs
                    </Text>
                </FormTitleContainer>

                <TextContainer>
                    <Text>
                        Le coût de l’éco-pâturage dépend de nombreux paramètres: surface des zones, linéaire de clôture,
                        type d’animaux, outils de communication, etc.
                    </Text>
                </TextContainer>

                <TextContainer>
                    <Text>
                        Remplissez le formulaire ci-dessous et nous vous recontacterons:
                    </Text>
                </TextContainer>

                <InputContainer>
                    <TextInput
                        placeholder={"Nom"}
                        isImportant
                        onTextChange={setInputLastnameValue}
                        value={inputLastnameValue}
                        error={lastnameError}
                    />
                </InputContainer>

                <InputContainer>
                    <TextInput
                        placeholder={"Prénom"}
                        isImportant
                        onTextChange={setInputNameValue}
                        value={inputNameValue}
                        error={nameError}
                    />
                </InputContainer>

                <InputContainer>
                    <SelectContainer>
                        <SelectInput
                            label={"Vous êtes ?"}
                            onChange={setSelectStatusValue}
                            options={[
                                {
                                    label: "Un particulier",
                                    value: "private_individual",
                                },
                                {
                                    label: "Une entreprise",
                                    value: "private_company"
                                },
                                {
                                    label: "Une collectivité",
                                    value: "public_company",
                                },
                            ]}
                        />
                    </SelectContainer>
                </InputContainer>

                <InputContainer>
                    <TextInput
                        placeholder={"Email"}
                        type={"email"}
                        isImportant
                        onTextChange={setInputEmailValue}
                        value={inputEmailValue}
                        error={emailError}
                    />
                </InputContainer>

                <InputContainer>
                    <TextInput
                        placeholder={"Téléphone"}
                        type={"tel"}
                        isImportant
                        onTextChange={setInputPhoneValue}
                        maxLength={20}
                        value={inputPhoneValue}
                        error={phoneError}
                        adornmentLeft={
                            <AdornmentContainer>
                                <Text>+33</Text>
                            </AdornmentContainer>
                        }
                    />
                </InputContainer>

                <InputContainer>
                    <TextInput
                        placeholder={"Estimation de la surface"}
                        type={"number"}
                        onTextChange={setInputAreaSizeValue}
                        value={inputAreaSizeValue}
                        error={areaError}
                        adornmentRight={
                            <AdornmentSelectContainer>
                                <SelectInput
                                    onChange={setSelectAreaUnitValue}
                                    options={[
                                        {
                                            label: "Mètre carré",
                                            value: "m2",
                                        },
                                        {
                                            label: "Are",
                                            value: "a",
                                        },
                                        {
                                            label: "Hectare",
                                            value: "ha"
                                        },
                                    ]}
                                />
                            </AdornmentSelectContainer>
                        }
                    />
                </InputContainer>

                <InputContainer>
                    <RadioContainer>
                                    <RadioInput
                                        onChange={setRadioWaterValue}
                                        label={"Accès à l’eau courante ?"}
                                        name={"water"}
                                        defaultValue={radioWaterValue ? 0 : 1}
                                        options={[
                                            {
                                                label: "Oui",
                                                value: true,
                                            },
                                {
                                    label: "Non",
                                    value: false,
                                },
                            ]}
                        />
                    </RadioContainer>
                </InputContainer>

                <InputContainer>
                    <RadioContainer>
                        <RadioInput
                            onChange={setRadioClosedAreaValue}
                            label={"La zone est-elle clôturée ?"}
                            name={"closed-area"}
                            defaultValue={radioClosedAreaValue ? 0 : 1}
                            options={[
                                {
                                    label: "Oui",
                                    value: true,
                                },
                                {
                                    label: "Non",
                                    value: false,
                                },
                            ]}
                        />
                    </RadioContainer>
                </InputContainer>

                <InputContainer>
                    <TextFieldInput
                        placeholder={"Information supplémentaire"}
                        onTextChange={setTextFieldMoreInfoValue}
                        value={textFieldMoreInfoValue}
                        rows={3}
                        error={moreInfoError}
                        maxLength={MORE_INFO_MAX_LENGTH}
                    />
                </InputContainer>

                <ButtonContainer>
                    <Button
                        onClick={handleSubmit}
                        loading={isSending}
                    >
                        Envoyer
                    </Button>
                </ButtonContainer>

                {/*<ButtonContainer>
                    <Button
                        onClick={() => {
                            const getRandType = () => {
                                const randType = Math.floor(Math.random() * 4)

                                switch (randType) {
                                    case 0:
                                        return "info"
                                    case 1:
                                        return "success"
                                    case 2:
                                        return "warning"
                                    case 3:
                                        return "error"
                                    default:
                                        return "info"
                                }
                            }

                            showNotification({
                                title: "Title",
                                message: "ceci est un test de message très long pour le message de la notif",
                                expire: Math.floor(Math.random() * 20) * 1000,
                                type: getRandType(),
                            })
                        }}
                        loading={isSending}
                    >
                        Envoyer test notif
                    </Button>
                </ButtonContainer>*/}
            </Section>
        </AppContainer>
    )
}

export default withNotification(View)

const TitleContainer = styled.div`
  margin-bottom: 25px;
`

const ButtonContainer = styled.div`
  margin-top: 50px;
`

const FormTitleContainer = styled.div`
  margin-bottom: 25px;
`

const TextContainer = styled.div`
  margin-bottom: 15px;
`

const BoldTextContainer = styled.div`
  margin-top: 15px;
  margin-bottom: 40px;
`

const InputContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 10px;
`

const SelectContainer = styled.div`
  width: 40%;

  > div {
    width: 40%;
  }

  @media (max-width: 768px) {
  width: 100%;

    > div {
      width: 100%;
    }
  }
`

const RadioContainer = styled.div`
  width: 40%;

  > div {
    width: 100%;
  }

  @media (max-width: 768px) {
  width: 100%;

    > div {
      width: 100%;
    }
  }
`

const AdornmentContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-end;
  margin-right: 10px;
  padding-bottom: 2px;
`

const AdornmentSelectContainer = styled.div`
  margin-left: 10px;
  display: flex;
  align-items: flex-end;
  width: 60%;

  @media (max-width: 768px) {

  }
`

const ImageContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 15px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`

const Image = styled.img`
  width: 100%;
  height: 100%;
  box-shadow: ${Colors.dropdownShadow};
  border-radius: .2%;
  
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    
  }
`
const InstallationContainer = styled.div`
    display: flex;
    flex-direction: column;
    
    width: 45%;
    height: 45%;
    margin: 0 2.5%;

    @media (max-width: 768px) {
      width: 100%;
      height: 100%;
      margin: 25px 0;
    }
`
